import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router'
import { ArrowLeft } from './Icons'

/** Double-bezel surface: an outer tray and an inner plate. Use `padding={false}` when the body is a table. */
export function Card({
  children,
  className = '',
  bodyClassName = '',
  padding = true,
  style,
}: {
  children: ReactNode
  className?: string
  bodyClassName?: string
  padding?: boolean
  style?: CSSProperties
}) {
  return (
    <section className={`shell ${className}`} style={style}>
      <div className={`plate ${padding ? 'p-5 sm:p-6' : ''} ${bodyClassName}`}>{children}</div>
    </section>
  )
}

/** Header row inside a Card: title, optional meta, optional right-side controls. */
export function CardHeader({ title, meta, children, className = '' }: { title: ReactNode; meta?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-3 ${className}`}>
      <div className="flex min-w-0 items-baseline gap-2.5">
        <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-ink-900">{title}</h2>
        {meta ? <span className="tabular text-[13px] text-ink-500">{meta}</span> : null}
      </div>
      {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
  )
}

type Tone = 'gray' | 'green' | 'amber' | 'red' | 'ink'

const tones: Record<Tone, { pill: string; dot: string }> = {
  gray: { pill: 'bg-ink-950/[0.05] text-ink-700', dot: 'bg-ink-400' },
  green: { pill: 'bg-live-50 text-live-700 ring-1 ring-inset ring-live-200/70', dot: 'bg-live-500' },
  amber: { pill: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100', dot: 'bg-warn-500' },
  red: { pill: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-100', dot: 'bg-danger-500' },
  ink: { pill: 'bg-ink-900 text-paper', dot: 'bg-live-400' },
}

export function Badge({ children, tone = 'gray', dot = false, mono = false, className = '' }: { children: ReactNode; tone?: Tone; dot?: boolean; mono?: boolean; className?: string }) {
  const t = tones[tone]
  return (
    <span
      className={`inline-flex h-6 items-center gap-1.5 rounded-[7px] px-2 text-[12px] font-medium leading-none tracking-[0.005em] ${mono ? 'font-mono' : ''} ${t.pill} ${className}`}
    >
      {dot ? <span className={`size-1.5 rounded-full ${t.dot}`} /> : null}
      {children}
    </span>
  )
}

/** Live-presence dot with an expanding pulse ring. */
export function LiveDot({ active = true, className = '' }: { active?: boolean; className?: string }) {
  return (
    <span className={`relative inline-flex size-2 ${className}`}>
      {active ? <span className="absolute inset-0 rounded-full bg-live-500 animate-pulse-ring" /> : null}
      <span className={`relative inline-flex size-2 rounded-full ${active ? 'bg-live-500' : 'bg-ink-300'}`} />
    </span>
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  back,
  badges,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  back?: { to: string; label: string }
  badges?: ReactNode
}) {
  return (
    <header className="mb-5 sm:mb-10">
      {back ? (
        <Link
          to={back.to}
          className="group mb-3 inline-flex sm:mb-4 items-center gap-1.5 text-[13px] font-medium text-ink-500 transition-colors duration-200 hover:text-ink-900"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 ease-spring group-hover:-translate-x-0.5" />
          {back.label}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 sm:gap-y-4">
        <div className="min-w-0">
          {eyebrow ? <div className="eyebrow mb-2.5">{eyebrow}</div> : null}
          <h1 className="text-[24px] font-semibold leading-[1.1] tracking-[-0.025em] text-ink-950 sm:text-[32px]">{title}</h1>
          {description ? <p className="mt-1.5 max-w-[60ch] text-[14px] leading-snug sm:mt-2 sm:text-[15px] sm:leading-relaxed text-ink-500">{description}</p> : null}
          {badges ? <div className="mt-3.5 flex flex-wrap items-center gap-1.5">{badges}</div> : null}
        </div>
        {actions ? <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">{actions}</div> : null}
      </div>
    </header>
  )
}

/** Big-number stat tile. */
export function Stat({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  className = '',
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  icon?: ReactNode
  tone?: 'default' | 'live'
  className?: string
}) {
  return (
    <div className={`plate flex flex-col justify-between gap-6 p-5 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        {icon ? <span className={`${tone === 'live' ? 'text-live-600' : 'text-ink-400'}`}>{icon}</span> : null}
      </div>
      <div>
        <div className="tabular text-[34px] font-semibold leading-none tracking-[-0.03em] text-ink-950">{value}</div>
        {hint ? <div className="mt-2 text-[13px] text-ink-500">{hint}</div> : null}
      </div>
    </div>
  )
}

/** Key-value list for detail metadata. */
export function MetaList({ items, className = '' }: { items: { label: string; value: ReactNode; mono?: boolean }[]; className?: string }) {
  return (
    <dl className={`grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-[13px] ${className}`}>
      {items.map((it) => (
        <div key={it.label} className="contents">
          <dt className="text-ink-500">{it.label}</dt>
          <dd className={`text-ink-900 ${it.mono ? 'font-mono tabular text-[12.5px]' : ''}`}>{it.value}</dd>
        </div>
      ))}
    </dl>
  )
}
